import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Eye, Activity, Network, MailX, ArrowRight } from "lucide-react";
import PublicShell from "components/layout/PublicShell";
import api from "lib/api";

const OPTOUT_EMAIL = "optout@securityscan.com.br";

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="text-justify text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function PublicScan() {
  const [ips, setIps] = useState([]);

  useEffect(() => {
    // título da aba (head) — não exibido no corpo da página.
    const prev = document.title;
    document.title = "Ethical Internet Scanning";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    // endpoint público (AllowAny) — IPs de varredura vindos do DB.
    api.get("/scan/ips").then((r) => setIps(r.data || [])).catch(() => setIps([]));
  }, []);

  const host = ips.find((x) => x.hostname)?.hostname || "scan.securityscan.com.br";

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-4 py-12">
        <header className="mb-10">
          <p className="font-mono text-xs uppercase tracking-widest text-primary">Transparência</p>
          <p className="mt-4 text-justify text-base text-muted-foreground">
            Esta página explica como o SecurityScan realiza varreduras na Internet, por que
            fazemos isso, qual é a natureza do tráfego gerado e como solicitar a exclusão
            (opt-out) dos seus ativos.
          </p>
          <nav className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <a href="#o-que" className="text-primary hover:underline">O que fazemos</a>
            <a href="#por-que" className="text-primary hover:underline">Por que escaneamos</a>
            <a href="#trafego" className="text-primary hover:underline">Natureza do tráfego</a>
            <a href="#ips" className="text-primary hover:underline">Nossos IPs</a>
            <a href="#optout" className="text-primary hover:underline">Opt-out</a>
          </nav>
        </header>

        <div className="space-y-10">
          <Section id="o-que" icon={Eye} title="O que fazemos">
            Realizamos varreduras <strong>controladas e éticas</strong> de serviços expostos
            publicamente na Internet, coletando apenas <strong>metadados</strong>: portas
            abertas, identificação de serviço/produto/versão, banners, certificados TLS e
            registros DNS. <strong>Não</strong> exploramos vulnerabilidades, não tentamos
            autenticação e não acessamos, alteramos ou exfiltramos dados.
          </Section>

          <Section id="por-que" icon={ShieldCheck} title="Por que escaneamos">
            Um serviço exposto em qualquer lugar do mundo pode se tornar um vetor de ataque.
            Mapear continuamente essa superfície de exposição dá aos defensores a visibilidade
            necessária para identificar e corrigir riscos antes que sejam abusados. O objetivo é
            <strong> defensivo</strong>: ajudar organizações a conhecer e reduzir sua própria
            exposição.
          </Section>

          <Section id="trafego" icon={Activity} title="Natureza do tráfego">
            Nosso tráfego é <strong>não intrusivo</strong>: tentativas de conexão TCP e
            handshakes de protocolo mínimos ou requisições de cabeçalho, apenas o suficiente
            para identificar o serviço. Não há força bruta, fuzzing, exploração de falhas nem
            envio de payloads maliciosos.
          </Section>

          <Section id="ips" icon={Network} title="Nossos IPs de varredura">
            <p>
              As varreduras se originam dos endereços abaixo (resolvem para{" "}
              <code className="rounded bg-secondary px-1 py-0.5 font-mono text-xs">{host}</code>).
              Você pode usar esta lista para reconhecer ou liberar nosso tráfego.
            </p>
            {ips.length > 0 ? (
              <pre className="mt-3 overflow-auto rounded-md border border-border bg-card p-3 font-mono text-xs text-foreground">
{ips.map((x) => x.ip).join("\n")}
              </pre>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">Nenhum IP de varredura cadastrado.</p>
            )}
          </Section>

          <Section id="optout" icon={MailX} title="Opt-out (exclusão)">
            Se você não deseja que seus ativos sejam incluídos nas varreduras, envie um e-mail
            para{" "}
            <a href={`mailto:${OPTOUT_EMAIL}`} className="text-primary hover:underline">{OPTOUT_EMAIL}</a>{" "}
            com os IPs ou faixas (CIDR) a excluir. As solicitações são processadas com brevidade.
          </Section>
        </div>

        <div className="mt-12 flex flex-col items-start gap-3 rounded-lg border border-border bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-lg font-semibold">Pare de reagir. Comece a prevenir.</div>
            <div className="text-sm text-muted-foreground">Conheça a sua superfície de ataque.</div>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Acessar a plataforma <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
